import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Paper, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Landmarks Bottom Sheet - M3 Themed
 * Allows users to drag landmark icons directly onto the map to create routes
 */

interface LandmarkIcon {
  id: string;
  label: string;
  icon: React.ReactNode;
  ariaLabel: string;
  color?: string;
}

interface LandmarksBottomSheetProps {
  open: boolean;
  onClose: () => void;
  landmarks: LandmarkIcon[];
  onDragStart: (landmarkId: string, event: React.DragEvent) => void;
  onDragEnd: () => void;
  darkMode?: boolean;
}

const SHEET_MIN_HEIGHT = 200; // Minimum visible height
const SHEET_MAX_HEIGHT = 500; // Maximum expansion height
const DRAG_HANDLE_SIZE = 4;
const ROUNDED_CORNERS = 24;

export const LandmarksBottomSheet: React.FC<LandmarksBottomSheetProps> = ({
  open,
  onClose,
  landmarks,
  onDragStart,
  onDragEnd,
  darkMode = false,
}) => {
  const [sheetHeight, setSheetHeight] = useState(SHEET_MIN_HEIGHT);
  const [isDraggingHandle, setIsDraggingHandle] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const initialYRef = useRef(0);

  // Handle dragging the sheet up/down
  const handleDragHandleMouseDown = (e: React.MouseEvent) => {
    setIsDraggingHandle(true);
    initialYRef.current = e.clientY;
  };

  useEffect(() => {
    if (!isDraggingHandle) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = initialYRef.current - e.clientY; // Negative = drag up, positive = drag down
      const newHeight = Math.min(Math.max(sheetHeight + delta, SHEET_MIN_HEIGHT), SHEET_MAX_HEIGHT);
      setSheetHeight(newHeight);
      initialYRef.current = e.clientY;
    };

    const handleMouseUp = () => {
      setIsDraggingHandle(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingHandle, sheetHeight]);

  // Handle swipe-down to close (touch)
  const handleTouchStart = useRef(0);

  const handleTouchEnd = (e: React.TouchEvent) => {
    const deltaY = e.changedTouches[0].clientY - handleTouchStart.current;
    if (deltaY > 50) {
      // Swiped down more than 50px
      onClose();
    }
  };

  const handleTouchStartCapture = (e: React.TouchEvent) => {
    handleTouchStart.current = e.touches[0].clientY;
  };

  if (!open) return null;

  const backdropColor = darkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.32)';
  const handleColor = darkMode ? 'rgba(255, 255, 255, 0.38)' : 'rgba(0, 0, 0, 0.38)';

  return (
    <>
      {/* Semi-transparent backdrop - taskbar behind it */}
      <Box
        onClick={onClose}
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: backdropColor,
          zIndex: 1300, // Just below sheet, but above taskbar
          backdropFilter: 'blur(2px)',
          animation: 'fadeIn 0.2s ease-out',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      />

      {/* Bottom Sheet */}
      <Paper
        ref={sheetRef}
        elevation={8}
        onTouchStart={handleTouchStartCapture}
        onTouchEnd={handleTouchEnd}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: `${sheetHeight}px`,
          zIndex: 1301, // Above backdrop
          borderTopLeftRadius: `${ROUNDED_CORNERS}px`,
          borderTopRightRadius: `${ROUNDED_CORNERS}px`,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          backgroundColor: 'transparent',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes slideUp': {
            from: { transform: 'translateY(100%)', opacity: 0 },
            to: { transform: 'translateY(0)', opacity: 1 },
          },
          touchAction: 'none',
        }}
      >
        {/* Drag Handle + Header */}
        <Box
          onMouseDown={handleDragHandleMouseDown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            cursor: isDraggingHandle ? 'grabbing' : 'grab',
            userSelect: 'none',
            borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
          }}
        >
          {/* Drag Handle Indicator */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              py: 0.5,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: DRAG_HANDLE_SIZE,
                borderRadius: 2,
                backgroundColor: handleColor,
              }}
            />
          </Box>

          {/* Close Button */}
          <Tooltip title="Close landmarks">
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                '&:hover': {
                  backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Landmarks Grid */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            px: 2,
            py: 2,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 90px)',
            gap: 1.5,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
            },
          }}
        >
          {landmarks.map((landmark) => (
            <Tooltip key={landmark.id} title={landmark.ariaLabel} placement="top">
              <Box
                draggable
                onDragStart={(e) => onDragStart(landmark.id, e)}
                onDragEnd={onDragEnd}
                aria-label={landmark.ariaLabel}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'grab',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                  '&:active': {
                    cursor: 'grabbing',
                    transform: 'scale(0.95)',
                  },
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    fontSize: 64,
                    color: landmark.color || (darkMode ? '#e0e0e0' : '#333'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {landmark.icon}
                </Box>

                {/* Label */}
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    textAlign: 'center',
                    color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                    lineHeight: 1.2,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {landmark.label}
                </span>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Paper>
    </>
  );
};

export default LandmarksBottomSheet;
